import React, { useState, useEffect } from "react";
import { X, Globe, User, Calendar, FileText, Image as ImageIcon } from "lucide-react";
import { useClickOutside } from "../../Hooks/useClickOutside";

const BlogDetailsModal = ({ isOpen, onClose, blog }) => {
  const [animate, setAnimate] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");

  // Helper function to get text in current language
  const getText = (field, lang = activeLanguage) => {
    if (!field) return "";
    if (typeof field === "object" && field !== null) {
      return field[lang] || field.en || field.toString() || "";
    }
    return field || "";
  };

  // Helper function to check if field is multilingual
  const isMultilingual = (field) => {
    return field && typeof field === "object" && !Array.isArray(field);
  };

  // Close modal with animation
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
      setActiveLanguage("en"); // Reset language on close
    }, 250);
  };

  // Handle click outside
  const domNode = useClickOutside(() => handleClose());

  // Animate modal entrance
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isOpen]);

  // Language tabs component
  const LanguageTabs = () => (
    <div className="flex space-x-2 mb-4">
      {["en", "hi", "es", "zh"].map((lang) => {
        const title = getText(blog?.title, lang);
        const content = getText(blog?.content, lang);
        const hasContent = title || content;
        
        return (
          <button
            key={lang}
            type="button"
            onClick={() => setActiveLanguage(lang)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
              activeLanguage === lang
                ? "bg-blue-500 text-white"
                : hasContent
                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                : "bg-gray-50 text-gray-400 cursor-not-allowed"
            }`}
            disabled={!hasContent}
            title={
              !hasContent 
                ? "Content not available in this language" 
                : `View in ${lang === "en" ? "English" : lang === "hi" ? "Hindi" : lang === "es" ? "Spanish" : "Chinese"}`
            }
          >
            {lang === "en" ? "EN" : 
             lang === "hi" ? "HI" : 
             lang === "es" ? "ES" : 
             "ZH"}
          </button>
        );
      })}
    </div>
  );

  // Conditional render AFTER hooks
  if (!isOpen || !blog) return null;

  const title = getText(blog.title);
  const content = getText(blog.content);
  const isMultiTitle = isMultilingual(blog.title);
  const isMultiContent = isMultilingual(blog.content);
  const hasMultipleLanguages = isMultiTitle || isMultiContent;

  return (
    <section
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        ref={domNode}
        className={`bg-white rounded-2xl shadow-xl w-[90%] md:w-[700px] lg:w-[800px] max-h-[90vh] overflow-hidden transform transition-all duration-300 flex flex-col ${
          animate ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-start px-6 py-4 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl md:text-2xl font-philosopher text-primaryTextColor line-clamp-2">
                {title || "Untitled Blog"}
              </h2>
              {hasMultipleLanguages && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
                  <Globe className="w-3 h-3" />
                  Multilingual
                </span>
              )}
            </div>
            
            {/* Blog Metadata */}
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span>{blog.author || "Admin"}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>
                  {new Date(blog.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              
              <div className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                <span>
                  {blog.updatedAt && blog.createdAt !== blog.updatedAt
                    ? "Updated: " + new Date(blog.updatedAt).toLocaleDateString("en-IN")
                    : "Created"}
                </span>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-red-500 transition ml-2 flex-shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Language Selector */}
        {hasMultipleLanguages && (
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">View Language:</span>
              </div>
              <LanguageTabs />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Currently viewing in:{" "}
              <span className="font-semibold">
                {activeLanguage === "en" ? "English" : 
                 activeLanguage === "hi" ? "Hindi" : 
                 activeLanguage === "es" ? "Spanish" : 
                 "Chinese"}
              </span>
            </p>
          </div>
        )}

        {/* Body - Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Blog Image */}
            <div className="w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
              {blog.image ? (
                <img
                  src={blog.image}
                  alt={title}
                  className="w-full h-auto max-h-96 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/800x400?text=Blog+Image";
                  }}
                />
              ) : (
                <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                  <ImageIcon className="w-12 h-12 mb-2" />
                  <p>No image available</p>
                </div>
              )}
            </div>

            {/* Language Availability Info */}
            {hasMultipleLanguages && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h3 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Available Languages
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {["en", "hi", "es", "zh"].map((lang) => {
                    const langTitle = getText(blog.title, lang);
                    const langContent = getText(blog.content, lang);
                    const hasContent = langTitle || langContent;
                    
                    return (
                      <div
                        key={lang}
                        className={`p-2 rounded ${
                          activeLanguage === lang
                            ? "bg-blue-100 border border-blue-300"
                            : "bg-white border"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">
                            {lang === "en" ? "English" : 
                             lang === "hi" ? "Hindi" : 
                             lang === "es" ? "Spanish" : 
                             "Chinese"}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            hasContent 
                              ? "bg-green-100 text-green-700" 
                              : "bg-gray-100 text-gray-500"
                          }`}>
                            {hasContent ? "✓" : "–"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 truncate">
                          {hasContent 
                            ? (langTitle || langContent || "Content available").substring(0, 30) + "..."
                            : "Not available"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Blog Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                Blog Content
              </h3>
              
              <div className="space-y-4">
                {/* Title in active language */}
                {title && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Title ({activeLanguage === "en" ? "English" : activeLanguage === "hi" ? "Hindi" : activeLanguage === "es" ? "Spanish" : "Chinese"}):</p>
                    <h4 className="text-xl font-medium text-gray-900">{title}</h4>
                  </div>
                )}

                {/* Content in active language */}
                {content ? (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Content:</p>
                    <div className="prose prose-blue max-w-none">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {content}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                    <p className="text-yellow-700">
                      No content available in {activeLanguage === "en" ? "English" : activeLanguage === "hi" ? "Hindi" : activeLanguage === "es" ? "Spanish" : "Chinese"}.
                      {hasMultipleLanguages && " Try switching to another language."}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Raw Data (for debugging/verification) */}
            <details className="border rounded-lg overflow-hidden">
              <summary className="bg-gray-100 px-4 py-2 cursor-pointer text-sm font-medium text-gray-700">
                View Raw Blog Data
              </summary>
              <div className="p-4 bg-gray-50 max-h-60 overflow-auto">
                <pre className="text-xs text-gray-600">
                  {JSON.stringify(blog, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div className="text-sm text-gray-500">
            Blog ID: <code className="text-xs bg-gray-100 px-2 py-1 rounded">{blog._id?.slice(-8)}</code>
          </div>
          
          <div className="flex gap-3">
            {hasMultipleLanguages && activeLanguage !== "en" && (
              <button
                onClick={() => setActiveLanguage("en")}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                View in English
              </button>
            )}
            
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogDetailsModal;